import type { Json } from '@/types/database';
import { supabase } from '@/lib/supabase';
import {
  parseResolvedQuizSponsorsStylePreset,
  parseSponsorsStylePresetCommandResult,
  parseSponsorsStylePresetWorkspace,
  type ResolvedQuizSponsorsStylePreset,
  type SponsorsStylePresetCommand,
  type SponsorsStylePresetCommandResult,
  type SponsorsStylePresetConfiguration,
  type SponsorsStylePresetWorkspace,
} from '@/lib/domain/sponsorsStylePreset';
import {
  sanitizeStyleOverrideFontFaces,
  type StyleOverrideFontFace,
} from '@/lib/embed/styleOverrides';

function unwrapJsonResult(data: unknown): unknown {
  return Array.isArray(data) && data.length === 1 ? data[0] : data;
}

export class SponsorsStylePresetClientError extends Error {
  constructor(
    public readonly code: 'revision_conflict' | 'field_validation',
    message: string,
    public readonly field?: 'sponsorship.footerUrl' | 'sponsorship.ctaUrl' | 'emailSignup.signupUrl'
  ) {
    super(message);
    this.name = 'SponsorsStylePresetClientError';
  }
}

function parseErrorDetails(details: unknown): Record<string, unknown> | null {
  if (typeof details !== 'string' || !details.trim()) return null;
  try {
    const value: unknown = JSON.parse(details);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function mapCommandError(error: {
  code?: string;
  message?: string;
  details?: string | null;
}): unknown {
  const details = parseErrorDetails(error.details);
  if (error.code === '40001' || details?.code === 'revision_conflict') {
    return new SponsorsStylePresetClientError(
      'revision_conflict',
      'This preset changed elsewhere.'
    );
  }
  if (
    details?.code === 'invalid_url' &&
    (details.field === 'sponsorship.footerUrl' ||
      details.field === 'sponsorship.ctaUrl' ||
      details.field === 'emailSignup.signupUrl')
  ) {
    return new SponsorsStylePresetClientError(
      'field_validation',
      typeof details.message === 'string' ? details.message : 'Enter a valid http or https URL.',
      details.field
    );
  }
  return error;
}

export interface SponsorsStylePresetClient {
  getWorkspace(input: { partnerId: string; quizId: string }): Promise<SponsorsStylePresetWorkspace>;
  execute(input: {
    partnerId: string;
    commandId: string;
    command: SponsorsStylePresetCommand;
  }): Promise<SponsorsStylePresetCommandResult>;
}

export async function getSponsorsStylePresetWorkspace(input: {
  partnerId: string;
  quizId: string;
}): Promise<SponsorsStylePresetWorkspace> {
  const { data, error } = await supabase.rpc('get_sponsors_style_preset_workspace', {
    p_partner_id: input.partnerId,
    p_quiz_id: input.quizId,
  });
  if (error) throw error;
  return parseSponsorsStylePresetWorkspace(unwrapJsonResult(data));
}

export async function executeSponsorsStylePresetCommand(input: {
  partnerId: string;
  commandId: string;
  command: SponsorsStylePresetCommand;
}): Promise<SponsorsStylePresetCommandResult> {
  const { data, error } = await supabase.rpc('execute_sponsors_style_preset_command', {
    p_partner_id: input.partnerId,
    p_command_id: input.commandId,
    p_command: input.command as unknown as Json,
  });
  if (error) throw mapCommandError(error);
  return parseSponsorsStylePresetCommandResult(unwrapJsonResult(data));
}

export async function resolveQuizSponsorsStylePreset(
  quizId: string
): Promise<ResolvedQuizSponsorsStylePreset> {
  const { data, error } = await supabase.rpc('resolve_quiz_sponsors_style_preset', {
    p_quiz_id: quizId,
  });
  if (error) throw error;
  return parseResolvedQuizSponsorsStylePreset(unwrapJsonResult(data));
}

export const sponsorsStylePresetClient: SponsorsStylePresetClient = {
  getWorkspace: getSponsorsStylePresetWorkspace,
  execute: executeSponsorsStylePresetCommand,
};

export function getSponsorsStylePresetFontFaces(
  configuration: SponsorsStylePresetConfiguration
): StyleOverrideFontFace[] {
  return sanitizeStyleOverrideFontFaces(
    configuration.fontAssets.map((asset) => ({
      family: asset.family,
      format: asset.format,
      src: supabase.storage.from(asset.storageBucket).getPublicUrl(asset.storagePath).data
        .publicUrl,
    }))
  );
}
